import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
} from 'react';
import { Realtime as AblyRealtime } from 'ably';
import { useSelector } from 'react-redux';
import { getAblyToken } from '../store/actions/user';
import type { RootState } from '../store';
import debug from '../utilities/debug';

// Singleton — persists across component remounts for the lifetime of the session
let globalClient: AblyRealtime | null = null;
let globalInitialized = false;

interface AblyContextValue {
  client: AblyRealtime | null;
  connected: boolean;
}

const AblyContext = createContext<AblyContextValue>({ client: null, connected: false });

export const useAblyClient = () => useContext(AblyContext);

export const AblyProvider = ({ children }: { children: React.ReactNode }) => {
  const authorized = useSelector((state: RootState) => state.session.authorized);
  const cognitoId = useSelector((state: RootState) => state.authentication.user?.cognito_id);

  const [connected, setConnected] = useState(() => globalClient?.connection?.state === 'connected');
  const isConfiguring = useRef(false);
  const connectionTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const retryTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const retryCount = useRef(0);

  const clearConnectionTimeout = useCallback(() => {
    if (connectionTimeout.current) {
      clearTimeout(connectionTimeout.current);
      connectionTimeout.current = null;
    }
  }, []);

  const init = useCallback(async () => {
    if (globalInitialized || isConfiguring.current) return;
    isConfiguring.current = true;
    globalInitialized = true;

    debug.log('ably', 'Fetching Ably token for mobile');
    const result = await getAblyToken();
    if (!result?.payload?.token) {
      debug.warn('Failed to get Ably token — real-time features offline', result?.message);
      globalInitialized = false;
      isConfiguring.current = false;
      // Retry with backoff (max 3 attempts) to recover from transient startup race conditions
      if (retryCount.current < 3) {
        const delay = Math.min(2000 * Math.pow(2, retryCount.current), 16000);
        retryCount.current += 1;
        retryTimeout.current = setTimeout(() => init(), delay);
      }
      return;
    }
    retryCount.current = 0;

    const initialToken = result.payload.token;

    connectionTimeout.current = setTimeout(() => {
      debug.warn('Ably connection timeout after 5s — continuing offline');
      clearConnectionTimeout();
      if (globalClient && globalClient.connection.state !== 'connected') {
        try { globalClient.close(); } catch {}
        globalClient = null;
      }
      globalInitialized = false;
      isConfiguring.current = false;
    }, 5000);

    try {
      const client = new AblyRealtime({
        token: initialToken,
        tls: true,
        authCallback: async (_tokenParams: any, callback: any) => {
          debug.log('ably', 'Token refresh requested');
          const refreshed = await getAblyToken();
          if (refreshed?.payload?.token) {
            callback(null, refreshed.payload.token);
          } else {
            callback(new Error('Failed to refresh Ably token'), null);
          }
        },
      });

      globalClient = client;

      client.connection.on('connected', () => {
        debug.log('ably', 'Connected');
        clearConnectionTimeout();
        isConfiguring.current = false;
        setConnected(true);
      });

      client.connection.on('failed', () => {
        debug.warn('Ably connection failed');
        clearConnectionTimeout();
        globalClient = null;
        globalInitialized = false;
        isConfiguring.current = false;
        setConnected(false);
      });

      client.connection.on('closed', () => {
        debug.log('ably', 'Connection closed');
        globalClient = null;
        globalInitialized = false;
        setConnected(false);
      });

      client.connection.on('disconnected', () => {
        debug.log('ably', 'Disconnected — will reconnect automatically');
        setConnected(false);
      });

      if (client.connection.state === 'connected') {
        clearConnectionTimeout();
        isConfiguring.current = false;
        setConnected(true);
      }
    } catch (err) {
      debug.error('Failed to initialize Ably client', err);
      clearConnectionTimeout();
      globalInitialized = false;
      isConfiguring.current = false;
    }
  }, [clearConnectionTimeout]);

  useEffect(() => {
    if (!authorized || !cognitoId) return;

    // Already initialized — sync state
    if (globalInitialized) {
      setConnected(globalClient?.connection?.state === 'connected');
      return;
    }

    init();

    return () => {
      // Don't close on unmount — singleton persists for the session
    };
  }, [authorized, cognitoId, init]);

  // Tear down on logout
  useEffect(() => {
    if (!authorized && globalClient) {
      debug.log('ably', 'Logged out — closing connection');
      if (retryTimeout.current) { clearTimeout(retryTimeout.current); retryTimeout.current = null; }
      retryCount.current = 0;
      try { globalClient.close(); } catch {}
      globalClient = null;
      globalInitialized = false;
      setConnected(false);
    }
  }, [authorized]);

  return (
    <AblyContext.Provider value={{ client: globalClient, connected }}>
      {children}
    </AblyContext.Provider>
  );
};

export default AblyContext;
